import { useState, memo } from 'react';
import Link from 'next/link';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import SyncCard from 'components/UI/SyncCard';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import { format } from 'date-fns';
import ArrowIcon from 'components/Icons/ArrowLeftIcon'; // UpArrowIcon
import PAGES from 'constants/links/pages';
import { getDays, calculateRate, compareByValue } from 'utils/utility';
import { cryptoMap } from 'config/contracts';
import { OrderType } from 'constants/types';

const useStyles = makeStyles(theme => ({
  root: {
    paddingTop: theme.spacing(1),
    overflowX: 'auto',
    width: '100%',
  },
  table: {
    width: '100%',
    textAlign: 'center',
    overflow: 'auto',
    '& thead th p': {
      whiteSpace: 'nowrap',
    },
    '& p': {
      padding: theme.spacing(0, 2),
    },
    '& th': {
      cursor: 'pointer',
    },
    '& th:hover': {
      opacity: 0.75,
    },
    '& th p': {
      display: 'flex',
      alignItems: 'center',
    },
  },
  tableRow: {
    cursor: 'pointer',
    boxShadow: 'inset -5px 2px 32px -9px #c5c5c5',
    animation: 'pulse 2s infinite',
  },
  active: {
    boxShadow: 'inset -5px 2px 32px -9px #06bcff',
  },
  link: {
    textDecoration: 'underline',
  },
  assetName: {
    // maxWidth: 240,
  },
  arrowUp: {
    transform: 'rotate(-90deg)',
    width: 16,
    height: 16,
  },
  arrowDown: {
    transform: 'rotate(90deg)',
    width: 16,
    height: 16,
  },
}));

function LoanList({ address, loans, loading, title = 'Loans' }) {
  const classes = useStyles();
  const [selectedRowIndex, setSelectedRowIndex] = useState();

  const [sortBy, setSortBy] = useState('');
  const [orderBy, setOrderBy] = useState(OrderType.DEFAULT);

  const changeRowHandler = index => () => {
    setSelectedRowIndex(index);
  };

  const sortHandler = key => () => {
    if (sortBy === key) {
      setOrderBy(
        orderBy === OrderType.DEFAULT
          ? OrderType.ASCENDING
          : orderBy === OrderType.ASCENDING
          ? OrderType.DESCENDING
          : OrderType.DEFAULT,
      );
    } else {
      setSortBy(key);
      setOrderBy(OrderType.ASCENDING);
    }
  };

  const TH = ({ sortKey, label, align }) => (
    <th onClick={sortKey ? sortHandler(sortKey) : null}>
      <Typography align={align}>
        {label}
        {sortBy === sortKey &&
          (orderBy === OrderType.ASCENDING ? (
            <ArrowIcon className={classes.arrowUp} />
          ) : orderBy === OrderType.DESCENDING ? (
            <ArrowIcon className={classes.arrowDown} />
          ) : null)}
      </Typography>
    </th>
  );

  const sortedLoans =
    orderBy === OrderType.DEFAULT
      ? loans
      : loans?.length
      ? loans
          .map((loan, index) => ({
            ...loan,
            index: index,
            tokenId: parseInt(loan.nftCollateralId),
            rate: calculateRate(loan.loanPrincipalAmount, loan.maximumRepaymentAmount),
          }))
          .sort(compareByValue(sortBy, orderBy === OrderType.ASCENDING))
      : [];

  return (
    <SyncCard>
      <Typography variant='h5' gutterBottom>
        {title}
      </Typography>
      {!loading && !loans?.length && <Typography>No Loans</Typography>}
      {!loading && loans?.length >= 1 && (
        <table className={classes.table}>
          <thead>
            <tr>
              <TH label='Asset' sortKey='tokenId' align='left' />
              <TH label='Status' sortKey='status' align='left' />
              <TH label='Lender' />
              <TH label='Borrower' />
              <TH label='Duration' sortKey='loanDuration' />
              <TH label='Due' sortKey='loanDueTime' align='center' />
              <TH label='Loan Value' sortKey='loanPrincipalAmount' align='right' />
              <TH label='Repayment' sortKey='maximumRepaymentAmount' align='right' />
              <TH label='Rate %' sortKey='rate' align='right' />
            </tr>
          </thead>
          <tbody>
            {(sortedLoans || []).map((loan, index) => (
              <tr
                key={index}
                className={clsx(classes.tableRow, index === selectedRowIndex && classes.active)}
                onClick={changeRowHandler(index)}>
                <td>
                  <Typography className={classes.assetName} align='left' noWrap>
                    <Link href={`${PAGES.ASSETS.url}/${loan.nftCollateralId}`}>
                      <a>#{loan.nftCollateralId}</a>
                    </Link>
                  </Typography>
                </td>
                <td>
                  <Typography className={classes.assetName} noWrap>
                    {loan.status}
                  </Typography>
                </td>
                <td>
                  <Typography align='left'>
                    <Link href={`${PAGES.ACCOUNT.url}/${loan.lender}`}>
                      <a className={classes.link}>
                        {loan?.lender === address ? 'You' : loan?.lender?.slice(0, 6)}
                      </a>
                    </Link>
                  </Typography>
                </td>
                <td>
                  <Typography align='left'>
                    <Link href={`${PAGES.ACCOUNT.url}/${loan.borrower}`}>
                      <a className={classes.link}>
                        {loan.borrower === address ? 'You' : loan?.borrower?.slice(0, 6)}
                      </a>
                    </Link>
                  </Typography>
                </td>
                <td>
                  <Typography>{getDays(loan.loanDuration)} days</Typography>
                </td>
                <td>
                  <Typography>{format(new Date(loan.loanDueTime), 'do MMM - H:mm')}</Typography>
                </td>
                <td>
                  <Typography align='right'>
                    {loan.loanPrincipalAmount / 1e18}{' '}
                    {cryptoMap[loan.loanERC20Denomination.toLowerCase()]}
                  </Typography>
                </td>
                <td>
                  <Typography align='right'>
                    {loan.maximumRepaymentAmount / 1e18}{' '}
                    {cryptoMap[loan.loanERC20Denomination.toLowerCase()]}
                  </Typography>
                </td>
                <td>
                  <Box display='flex' alignItems='center' gap='small' justifyContent='flex-end'>
                    <Typography align='right'>
                      {calculateRate(
                        loan.loanPrincipalAmount,
                        loan.maximumRepaymentAmount,
                        // getDays(loan.loanDuration),
                      )}
                      %
                    </Typography>
                  </Box>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </SyncCard>
  );
}

export default memo(LoanList);
